import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { deactivateEmployeeAction, deleteEmployeeAction, getEmployeeAction, getFilteredEmployeeAction } from "../../../store/actions/employee/EmployeeActions";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import ShimmerLoader from "../../pages/ShimmerLoader/ShimmerLoader";
import Styles from "./Employee.module.css";
import NoDataFound from "../common/NoDataFound";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import copy from "copy-to-clipboard";
import { toast } from "react-toastify";

const searchSchema = Yup.object().shape({
  searchKey: Yup.string().required("Select a search key"),
  searchValue: Yup.string()
    .required("Enter a search value")
    .max(30, "Search value cannot exceed 30 characters")
    .test("not-blank", "Search value cannot be blank", (value) => value && value.trim() !== ""),
});

const Userlist = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useState({ searchKey: "emp_id", searchValue: "" });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const countPerPage = Number(process.env.REACT_APP_ITEMS_PER_PAGE);

  useEffect(() => {
    if (searchParams.searchKey && searchParams.searchValue) {
      const payload = {
        module_name: "employee",
        search_key: searchParams.searchKey,
        search_value: searchParams.searchValue,
        page_number: currentPage,
      };
      dispatch(getFilteredEmployeeAction(payload, navigate));
    } else {
      const payload = {
        page_number: currentPage,
        role: 0,
        order_by: "updated_at",
        order_direction: "DESC",
      };
      dispatch(getEmployeeAction(payload, navigate));
    }
  }, [dispatch, currentPage, searchParams]);

  const { employees, error, loading, message, totalCount } = useSelector((state) => state.employee);

  const onPageChange = (increment) => {
    setCurrentPage((prevPage) => Math.max(1, prevPage + increment));
  };

  const totalPages = Math.ceil(totalCount / countPerPage);

  const deleteEmployee = (userId) => {
    Swal.fire({
      title: "Are you sure?",
      text: `You want to delete this user?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        let newEmpPayload = {
          page_number: currentPage,
          role: 0,
          order_by: "updated_at",
          order_direction: "DESC",
        };
        dispatch(deleteEmployeeAction(userId, navigate, newEmpPayload));
      }
    });
  };

  const handleStatusChange = (userId, isActive) => {
    let status = isActive ? "deactivate" : "activate";
    Swal.fire({
      title: "Are you sure?",
      text: `You want to ${status} this user?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        let payload = {
          is_active: !isActive,
        };
        let getEmpPayload = {
          page_number: currentPage,
          role: 0,
          order_by: "updated_at",
          order_direction: "DESC",
        };
        dispatch(deactivateEmployeeAction(payload, userId, navigate, getEmpPayload));
      }
    });
  };

  const handleRowClick = (id) => {
    navigate(`/user-details/${id}`);
  };

  const navigateToAddUserPage = () => {
    navigate("/add-user");
  };

  const handleCopyToClipboard = (empId, event) => {
    event.stopPropagation();
    copy(empId);
    toast.success(`Copied  to clipboard!`, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 2000,
    });
  };

  return (
    <>
      <div className='row page-titles'>
        <ol className='breadcrumb'>
          <li className='breadcrumb-item active'>
            <Link to={"#"}>Users</Link>
          </li>
        </ol>
      </div>

      <div className='row'>
        <div className='col-lg-12'>
          <div className='card'>
            <div className='card-header'>
              <h4 className='card-title'>All Users</h4>
              <div className='d-flex'>
                <Formik
                  initialValues={{ searchKey: "emp_id", searchValue: "" }}
                  validationSchema={searchSchema}
                  onSubmit={(values) => {
                    const trimmedSearchValue = values && values.searchValue.trim();
                    setCurrentPage(1);
                    setSearchParams({ searchKey: values.searchKey, searchValue: trimmedSearchValue });
                  }}
                  onReset={() => {
                    setCurrentPage(1);
                    setSearchParams({ searchKey: "", searchValue: "" });
                  }}
                >
                  {({ handleReset, touched, errors, values }) => (
                    <Form className='d-flex'>
                      <Field as='select' name='searchKey' className={`form-select me-2 ${Styles.fixedWidth} `}>
                        {/* <option value='' disabled>
                          Select One
                        </option> */}
                        <option value='emp_id'>Employee ID</option>
                        <option value='name'>Name</option>
                        <option value='email'>Email</option>
                        <option value='role_name'>Role Name</option>
                      </Field>

                      <div className='position-relative flex-grow-1 me-2'>
                        <Field type='text' name='searchValue' className={`form-control ps-4 pe-5 ${touched.searchValue && errors.searchValue ? "is-invalid" : ""}`} maxLength={65} placeholder='Search' aria-label='Search' />
                      </div>
                      <button className='btn btn-primary mx-1' type='submit' disabled={!values.searchKey || !values.searchValue.trim()}>
                        Search
                      </button>
                      <button type='reset' className='btn btn-outline-secondary mx-1' onClick={handleReset} disabled={!values.searchKey && !values.searchValue.trim()}>
                        Clear
                      </button>
                    </Form>
                  )}
                </Formik>
                <button className='btn btn-outline-secondary mx-1' type='button' onClick={navigateToAddUserPage}>
                  Add User
                </button>
              </div>
            </div>

            <div className='card-body'>
              <div className='table-responsive ticket-table'>
                <div id='ticket_wrapper' className='dataTables_wrapper no-footer'>
                  <div className='table-responsive'>
                    <table id='example' className='display dataTablesCard table-responsive-xl dataTable no-footer w-100'>
                      <thead>
                        <tr>
                          <th className={`${Styles.largeWidth}`}>Employee ID</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role Name</th>
                          <th>City</th>
                          <th className={`text-center ${Styles.smallWidth}`}>Active/Inactive</th>
                          <th className={`${Styles.smallWidth}`}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={7} className='text-center'>
                              <ShimmerLoader width='100%' height='20%' count={4} />
                            </td>
                          </tr>
                        ) : employees.length > 0 ? (
                          employees.map((item, index) => (
                            <tr key={index}>
                              <td style={{ cursor: "pointer" }}>
                                <Link className={`${Styles.link}`} to={`/user-details/${item.id}`}>
                                  {item.emp_id}
                                </Link>
                                <svg style={{ marginLeft: "8px", cursor: "pointer" }} onClick={(e) => handleCopyToClipboard(item.emp_id, e)} xmlns='http://www.w3.org/2000/svg' height='24px' viewBox='0 -960 960 960' width='24px' fill='#c3992e'>
                                  <path d='M362.31-260Q332-260 311-281q-21-21-21-51.31v-455.38Q290-818 311-839q21-21 51.31-21h335.38Q728-860 749-839q21 21 21 51.31v455.38Q770-302 749-281q-21 21-51.31 21H362.31Zm0-60h335.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-455.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H362.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v455.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85Zm-140 200Q192-120 171-141q-21-21-21-51.31v-515.38h60v515.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h395.38v60H222.31ZM350-320v-480 480Z' />
                                </svg>
                              </td>
                              <td>{item.name}</td>
                              <td>{item.email}</td>
                              <td>{item.role_name}</td>
                              <td>{item.city}</td>
                              <td className='d-flex justify-content-center'>
                                <div className='form-check form-switch'>
                                  <input className='form-check-input' style={{ cursor: "pointer" }} type='checkbox' role='switch' id={`flexSwitchCheckChecked-${item.id}`} checked={item.is_active} onChange={() => handleStatusChange(item.id, item.is_active)} />
                                  <label className='form-check-label' htmlFor={`flexSwitchCheckChecked-${item.id}`}></label>
                                </div>
                              </td>
                              <td>
                                <div className='d-flex justify-content-start'>
                                  <Link to={`/edit-user/${item.id}`} className='btn btn-primary shadow btn-xs sharp me-1'>
                                    <i className='fas fa-pencil-alt'></i>
                                  </Link>
                                  <button className='btn btn-danger shadow btn-xs sharp mx-2' onClick={() => deleteEmployee(item.id)}>
                                    <i className='fa fa-trash'></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr className={`${Styles.no_hover}`}>
                            <td className={`fs-3 text-center`} colSpan={7}>
                              <NoDataFound width={500} />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {!loading && employees.length > 0 && (
                    <div className='d-flex justify-content-end align-items-center px-4'>
                      <div className='d-sm-flex justify-content-end align-items-center ms-3 mt-3 mb-3'>
                        <div className='dataTables_paginate paging_simple_numbers mb-0 d-flex align-items-center' id='example2_paginate'>
                          <i className={`fa fa-chevron-circle-left fa-2x ${currentPage === 1 ? "text-muted" : "text-primary"}`} onClick={() => currentPage > 1 && onPageChange(-1)} style={{ cursor: currentPage === 1 ? "not-allowed" : "pointer" }}></i>
                          <span className='mx-2'>
                            Page {currentPage} of {totalPages}
                          </span>
                          <i className={`fa fa-chevron-circle-right fa-2x ${currentPage === totalPages || totalPages === 0 ? "text-muted" : "text-primary"}`} onClick={() => currentPage < totalPages && onPageChange(1)} style={{ cursor: currentPage === totalPages || totalPages === 0 ? "not-allowed" : "pointer" }}></i>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Userlist;

//old code

// import React, { useState, useEffect, useRef } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { deactivateEmployeeAction, deleteEmployeeAction, getEmployeeAction, getFilteredEmployeeAction } from "../../../store/actions/employee/EmployeeActions";
// import { useDispatch, useSelector } from "react-redux";
// import Swal from "sweetalert2";
// import ShimmerLoader from "../../pages/ShimmerLoader/ShimmerLoader";
// import Styles from "./Employee.module.css";
// import NoDataFound from "../common/NoDataFound";
// import { Formik, Form, Field } from "formik";
// import * as Yup from "yup";

// const searchSchema = Yup.object().shape({
//   searchKey: Yup.string().required("Select a search key"),
//   searchValue: Yup.string().required("Enter a search value").max(30, "Search value cannot exceed 30 characters"),
// });

// const Userlist = () => {
//   const [currentPage, setCurrentPage] = useState(1);
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const countPerPage = Number(process.env.REACT_APP_ITEMS_PER_PAGE);

//   useEffect(() => {
//     let payload = {
//       page_number: currentPage,
//       role: 0,
//       order_by: "updated_at",
//       order_direction: "DESC", // 'ASC', 'DESC'
//     };
//     dispatch(getEmployeeAction(payload, navigate));
//   }, [dispatch, currentPage]);

//   const { employees, error, loading, message, totalCount } = useSelector((state) => state.employee);

//   const onPageChange = (increment) => {
//     setCurrentPage((prevPage) => Math.max(1, prevPage + increment));
//   };

//   const totalPages = Math.ceil(totalCount / countPerPage);

//   const deleteEmployee = (userId) => {
//     Swal.fire({
//       title: "Are you sure?",
//       text: `You want to delete this user?`,
//       icon: "warning",
//       showCancelButton: true,
//       confirmButtonColor: "#3085d6",
//       cancelButtonColor: "#d33",
//       confirmButtonText: "Yes",
//       cancelButtonText: "No",
//     }).then((result) => {
//       if (result.isConfirmed) {
//         let newEmpPayload = {
//           page_number: currentPage,
//           role: 0,
//           order_by: "updated_at",
//           order_direction: "DESC", // 'ASC', 'DESC'
//         };
//         dispatch(deleteEmployeeAction(userId, navigate, newEmpPayload));
//       }
//     });
//   };

//   const handleStatusChange = (userId, isActive) => {
//     let status = isActive ? "deactivate" : "activate";
//     Swal.fire({
//       title: "Are you sure?",
//       text: `You want to ${status} this user?`,
//       icon: "warning",
//       showCancelButton: true,
//       confirmButtonColor: "#3085d6",
//       cancelButtonColor: "#d33",
//       confirmButtonText: "Yes",
//       cancelButtonText: "No",
//     }).then((result) => {
//       if (result.isConfirmed) {
//         let payload = {
//           is_active: !isActive,
//         };
//         let getEmpPayload = {
//           page_number: currentPage,
//           role: 0,
//           order_by: "updated_at",
//           order_direction: "DESC", // 'ASC', 'DESC'
//         };
//         dispatch(deactivateEmployeeAction(payload, userId, navigate, getEmpPayload));
//       }
//     });
//   };

//   const handleRowClick = (id) => {
//     navigate(`/user-details/${id}`);
//   };

//   const navigateToAddUserPage = () => {
//     navigate("/add-user");
//   };

//   return (
//     <>
//       <div className='row page-titles'>
//         <ol className='breadcrumb'>
//           <li className='breadcrumb-item active'>
//             <Link to={"#"}>Users</Link>
//           </li>
//         </ol>
//       </div>

//       <div className='row'>
//         <div className='col-lg-12'>
//           <div className='card'>
//             <div className='card-header'>
//               <h4 className='card-title'>All Users</h4>
//               <div className='d-flex'>
//                 <Formik
//                   initialValues={{ searchKey: "", searchValue: "" }}
//                   validationSchema={searchSchema}
//                   onSubmit={(values) => {
//                     setCurrentPage(1);
//                     const payload = {
//                       module_name: "employee",
//                       search_key: values.searchKey,
//                       search_value: values.searchValue,
//                       page_number: 1,
//                     };
//                     dispatch(getFilteredEmployeeAction(payload, navigate));
//                   }}
//                   onReset={() => {
//                     setCurrentPage(1);
//                     const payload = {
//                       page_number: 1,
//                       role: 0,
//                       order_by: "updated_at",
//                       order_direction: "DESC",
//                     };
//                     dispatch(getEmployeeAction(payload, navigate));
//                   }}
//                 >
//                   {({ handleReset, touched, errors, values }) => (
//                     <Form className='d-flex'>
//                       <Field as='select' name='searchKey' className={`form-select me-2 ${Styles.fixedWidth} `}>
//                         <option value=''>Select One</option>
//                         <option value='name'>Name</option>
//                         <option value='emp_id'>User Id</option>
//                         <option value='email'>Email</option>
//                         <option value='role_name'>Role Name</option>
//                       </Field>

//                       <div className='position-relative flex-grow-1 me-2'>
//                         <Field type='text' name='searchValue' className={`form-control ps-4 pe-5 ${touched.searchValue && errors.searchValue ? "is-invalid" : ""}`} maxLength={30} placeholder='Search' aria-label='Search' />
//                       </div>
//                       <button className='btn btn-primary mx-1' type='submit' disabled={!values.searchKey || !values.searchValue}>
//                         Search
//                       </button>
//                       <button type='reset' className='btn btn-outline-secondary mx-1' onClick={handleReset} disabled={!values.searchKey && !values.searchValue}>
//                         Clear
//                       </button>
//                     </Form>
//                   )}
//                 </Formik>
//                 <button className='btn btn-outline-secondary mx-1' type='button' onClick={navigateToAddUserPage}>
//                   Add User
//                 </button>
//               </div>
//             </div>

//             <div className='card-body'>
//               <div className='table-responsive ticket-table'>
//                 <div id='ticket_wrapper' className='dataTables_wrapper no-footer'>
//                   <div className='table-responsive'>
//                     <table id='example' className='display dataTablesCard table-responsive-xl dataTable no-footer w-100'>
//                       <thead>
//                         <tr>
//                           <th className={`${Styles.largeWidth}`}>User ID</th>
//                           <th>Name</th>
//                           <th>Email</th>
//                           <th>Role Name</th>
//                           <th>City</th>
//                           <th className={`text-center ${Styles.smallWidth}`}>Active/Inactive</th>
//                           <th className={`${Styles.smallWidth}`}>Actions</th>
//                         </tr>
//                       </thead>
//                       <tbody>
//                         {loading ? (
//                           <tr>
//                             <td colSpan={7} className='text-center'>
//                               <ShimmerLoader width='100%' height='20%' count={4} />
//                             </td>
//                           </tr>
//                         ) : employees.length > 0 ? (
//                           employees.map((item, index) => (
//                             <tr key={index}>
//                               <td onClick={() => handleRowClick(item.id)} style={{ cursor: "pointer" }}>
//                                 <Link className={`${Styles.link}`} to={`/user-details/${item.id}`}>
//                                   {item.emp_id}
//                                 </Link>
//                               </td>
//                               <td>{item.name}</td>
//                               <td>{item.email}</td>
//                               <td>{item.role_name}</td>
//                               <td>{item.city}</td>
//                               <td className='d-flex justify-content-center'>
//                                 <div className='form-check form-switch'>
//                                   <input className='form-check-input' type='checkbox' role='switch' id={`flexSwitchCheckChecked-${item.id}`} checked={item.is_active} onChange={() => handleStatusChange(item.id, item.is_active)} />
//                                   <label className='form-check-label' htmlFor={`flexSwitchCheckChecked-${item.id}`}></label>
//                                 </div>
//                               </td>
//                               <td>
//                                 <div className='d-flex justify-content-start'>
//                                   <Link to={`/edit-user/${item.id}`} className='btn btn-primary shadow btn-xs sharp me-1'>
//                                     <i className='fas fa-pencil-alt'></i>
//                                   </Link>
//                                   <button className='btn btn-danger shadow btn-xs sharp mx-2' onClick={() => deleteEmployee(item.id)}>
//                                     <i className='fa fa-trash'></i>
//                                   </button>
//                                 </div>
//                               </td>
//                             </tr>
//                           ))
//                         ) : (
//                           <tr className={`${Styles.no_hover}`}>
//                             <td className={`fs-3 text-center`} colSpan={7}>
//                               <NoDataFound width={500} />
//                             </td>
//                           </tr>
//                         )}
//                       </tbody>
//                     </table>
//                   </div>

//                   {!loading && employees.length > 0 && (
//                     <div className='d-flex justify-content-end align-items-center px-4'>
//                       <div className='d-sm-flex justify-content-end align-items-center ms-3 mt-3 mb-3'>
//                         <div className='dataTables_paginate paging_simple_numbers mb-0 d-flex align-items-center' id='example2_paginate'>
//                           <i className={`fa fa-chevron-circle-left fa-2x ${currentPage === 1 ? "text-muted" : "text-primary"}`} onClick={() => currentPage > 1 && onPageChange(-1)} style={{ cursor: currentPage === 1 ? "not-allowed" : "pointer" }}></i>
//                           <span className='mx-2'>
//                             Page {currentPage} of {totalPages}
//                           </span>
//                           <i className={`fa fa-chevron-circle-right fa-2x ${currentPage === totalPages || totalPages === 0 ? "text-muted" : "text-primary"}`} onClick={() => currentPage < totalPages && onPageChange(1)} style={{ cursor: currentPage === totalPages || totalPages === 0 ? "not-allowed" : "pointer" }}></i>
//                         </div>
//                       </div>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default Userlist;
